/* Gobivo Purple Studio 3.0 */
(() => {
  'use strict';

  const configEl = document.getElementById('GobivoConfig');
  const config = configEl ? JSON.parse(configEl.textContent) : { routes: { root: '/' }, strings: {} };
  const routes = config.routes;
  const root = routes.root.endsWith('/') ? routes.root : routes.root + '/';
  const strings = config.strings || {};
  const supportsDialog = typeof HTMLDialogElement === 'function';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Helpers ---------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const store = {
    get(key, fallback) { try { const v = localStorage.getItem(key); return v === null ? fallback : JSON.parse(v); } catch (e) { return fallback; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage unavailable */ } }
  };

  let toastTimer;
  function toast(message, isError = false) {
    const el = $('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.classList.toggle('is-error', isError);
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 3200);
  }

  /* ---------- Dialogs (drawers & modals) ---------- */
  const openers = new WeakMap();
  function openDialog(dialog, opener) {
    if (!dialog || !supportsDialog) return false;
    if (!dialog.open) {
      openers.set(dialog, opener || document.activeElement);
      dialog.classList.remove('is-closing');
      dialog.showModal();
      document.body.classList.add('has-overlay');
    }
    return true;
  }
  function closeDialog(dialog) {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    const finish = () => {
      dialog.classList.remove('is-closing');
      dialog.close();
    };
    if (reducedMotion) return finish();
    dialog.classList.add('is-closing');
    setTimeout(finish, 280);
  }
  document.addEventListener('close', (e) => {
    const dialog = e.target;
    if (!(dialog instanceof HTMLElement) || dialog.tagName !== 'DIALOG') return;
    if (!$$('dialog[open]').length) document.body.classList.remove('has-overlay');
    const opener = openers.get(dialog);
    if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus({ preventScroll: true });
    if (dialog.classList.contains('lightbox')) dialog.remove();
  }, true);
  document.addEventListener('cancel', (e) => {
    if (e.target.tagName === 'DIALOG') { e.preventDefault(); closeDialog(e.target); }
  }, true);
  document.addEventListener('click', (e) => {
    const dialog = e.target;
    if (dialog.tagName === 'DIALOG' && dialog.open) {
      const r = dialog.getBoundingClientRect();
      const outside = e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
      if (outside || dialog.classList.contains('lightbox')) closeDialog(dialog);
    }
    const closer = e.target.closest('[data-drawer-close], [data-modal-close], .lightbox img');
    if (closer) closeDialog(closer.closest('dialog'));
  });

  /* ---------- Announcement bar ---------- */
  function initAnnouncement(scope = document) {
    $$('[data-announcement]', scope).forEach((bar) => {
      if (bar.dataset.ready) return;
      bar.dataset.ready = 'true';
      const items = $$('.announcement-item', bar);
      if (items.length < 2) return;
      let index = 0;
      let timer;
      const show = (i) => {
        index = (i + items.length) % items.length;
        items.forEach((item, n) => {
          item.classList.toggle('is-active', n === index);
          item.setAttribute('aria-hidden', n === index ? 'false' : 'true');
        });
      };
      const start = () => {
        clearInterval(timer);
        if (!reducedMotion) timer = setInterval(() => show(index + 1), (Number(bar.dataset.speed) || 5) * 1000);
      };
      $('[data-announcement-prev]', bar)?.addEventListener('click', () => { show(index - 1); start(); });
      $('[data-announcement-next]', bar)?.addEventListener('click', () => { show(index + 1); start(); });
      bar.addEventListener('mouseenter', () => clearInterval(timer));
      bar.addEventListener('mouseleave', start);
      bar.addEventListener('focusin', () => clearInterval(timer));
      document.addEventListener('shopify:block:select', (e) => {
        const i = items.indexOf(e.target);
        if (i > -1) { clearInterval(timer); show(i); }
      });
      start();
    });
  }

  /* ---------- Header ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const setHeight = () => document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    setHeight();
    window.addEventListener('resize', debounce(setHeight, 150));
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        header.classList.toggle('is-scrolled', window.scrollY > 10);
        const btt = $('[data-back-to-top]');
        if (btt) btt.hidden = window.scrollY < 900;
        ticking = false;
      });
    }, { passive: true });

    // Dropdowns: hover on desktop pointers, click everywhere
    const hoverCapable = window.matchMedia('(hover: hover)').matches;
    $$('details[data-hover-menu]', header).forEach((menu) => {
      let closeTimer;
      if (hoverCapable) {
        menu.addEventListener('mouseenter', () => { clearTimeout(closeTimer); closeOthers(menu); menu.open = true; });
        menu.addEventListener('mouseleave', () => { closeTimer = setTimeout(() => { menu.open = false; }, 160); });
      }
      menu.addEventListener('toggle', () => { if (menu.open) closeOthers(menu); });
    });
    function closeOthers(current) {
      $$('details[data-hover-menu][open]', header).forEach((d) => { if (d !== current) d.open = false; });
    }
    document.addEventListener('click', (e) => {
      if (!e.target.closest('details[data-hover-menu]')) closeOthers(null);
    });

    // Search overlay
    const overlay = $('[data-search-overlay]', header);
    $$('[data-search-open]', header).forEach((btn) => btn.addEventListener('click', () => {
      if (!overlay) return;
      overlay.hidden = !overlay.hidden;
      if (!overlay.hidden) $('input[type=search]', overlay)?.focus();
    }));
    $('[data-search-close]', header)?.addEventListener('click', () => { if (overlay) overlay.hidden = true; });

    // Mobile menu
    $$('[data-menu-open]').forEach((btn) => btn.addEventListener('click', () => openDialog($('#MenuDrawer'), btn)));
  }

  /* ---------- Predictive search ---------- */
  function initPredictiveSearch(scope = document) {
    if (!config.predictiveSearch || !routes.predictiveSearch) return;
    $$('[data-predictive-search]', scope).forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = 'true';
      const input = $('[data-predictive-input]', wrap);
      const results = $('[data-predictive-results]', wrap);
      if (!input || !results) return;
      let controller;
      const close = () => { results.hidden = true; input.setAttribute('aria-expanded', 'false'); };
      const search = debounce(async () => {
        const q = input.value.trim();
        if (q.length < 2) return close();
        controller?.abort();
        controller = new AbortController();
        results.classList.add('is-loading');
        const params = new URLSearchParams({ q, section_id: 'predictive-search' });
        params.set('resources[type]', 'product,collection,page,article,query');
        params.set('resources[limit]', '6');
        params.set('resources[limit_scope]', 'each');
        try {
          const res = await fetch(`${routes.predictiveSearch}?${params}`, { signal: controller.signal });
          if (!res.ok) throw new Error(res.status);
          const doc = parseHTML(await res.text());
          const inner = doc.querySelector('.predictive-inner');
          if (!inner) return close();
          results.replaceChildren(inner);
          results.hidden = false;
          input.setAttribute('aria-expanded', 'true');
        } catch (err) {
          if (err.name !== 'AbortError') close();
        } finally {
          results.classList.remove('is-loading');
        }
      }, 260);
      input.addEventListener('input', search);
      input.addEventListener('focus', () => { if (results.childElementCount && input.value.trim().length > 1) { results.hidden = false; input.setAttribute('aria-expanded', 'true'); } });
      wrap.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { close(); input.focus(); return; }
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        const links = $$('a', results);
        if (results.hidden || !links.length) return;
        e.preventDefault();
        const current = links.indexOf(document.activeElement);
        const next = e.key === 'ArrowDown' ? Math.min(current + 1, links.length - 1) : current - 1;
        if (next < 0) input.focus(); else links[next].focus();
      });
      document.addEventListener('click', (e) => { if (!wrap.contains(e.target)) close(); });
    });
  }

  /* ---------- Cart ---------- */
  const cartDrawer = () => $('[data-cart-drawer]');
  const useDrawer = () => config.cartType !== 'page' && supportsDialog && !!cartDrawer();

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      const changed = el.textContent !== String(count);
      el.textContent = count;
      el.hidden = Number(count) === 0;
      if (changed && !reducedMotion) { el.classList.remove('is-bumped'); void el.offsetWidth; el.classList.add('is-bumped'); }
    });
    $$('.cart-link').forEach((el) => {
      const label = el.getAttribute('aria-label') || '';
      el.setAttribute('aria-label', label.replace(/\(\d+\)/, `(${count})`));
    });
  }

  function renderCart(html) {
    const drawer = cartDrawer();
    if (!drawer || !html) return;
    const fresh = parseHTML(html).querySelector('[data-cart-inner]');
    const current = $('[data-cart-inner]', drawer);
    if (fresh && current) {
      current.replaceWith(fresh);
      updateCartCount(fresh.dataset.cartCountValue);
    }
  }

  async function refreshCart() {
    try {
      const res = await fetch(`${root}?section_id=cart-drawer`);
      if (res.ok) renderCart(await res.text());
    } catch (e) { /* keep current markup */ }
  }

  function openCart(opener) {
    if (!useDrawer()) { window.location.href = routes.cart; return; }
    const modal = $('#QuickView');
    if (modal?.open) modal.close();
    openDialog(cartDrawer(), opener);
  }

  async function changeLine(key, quantity, line) {
    const drawer = cartDrawer();
    const error = drawer && $('[data-cart-error]', drawer);
    if (line) line.classList.add('is-updating');
    try {
      const res = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: 'cart-drawer', sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.description === 'string' ? data.description : strings.error);
      if ($('[data-cart-page]')) { window.location.reload(); return; }
      renderCart(data.sections && data.sections['cart-drawer']);
    } catch (err) {
      await refreshCart();
      const freshError = $('[data-cart-error]', cartDrawer() || document) || error;
      if (freshError) { freshError.textContent = err.message || strings.error; freshError.hidden = false; }
      else toast(err.message || strings.error, true);
      line?.classList.remove('is-updating');
    }
  }

  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-cart-open]');
    if (open && useDrawer() && !$('[data-cart-page]')) { e.preventDefault(); openCart(open); return; }

    const qtyBtn = e.target.closest('[data-cart-qty]');
    if (qtyBtn) {
      const line = qtyBtn.closest('[data-line-key]');
      const quantity = Math.max(0, parseInt(qtyBtn.dataset.cartQty, 10) || 0);
      if (line) changeLine(line.dataset.lineKey, quantity, line);
      return;
    }
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      const line = remove.closest('[data-line-key]');
      if (line) { e.preventDefault(); changeLine(line.dataset.lineKey, 0, line); }
    }
  });

  document.addEventListener('change', (e) => {
    const qtyInput = e.target.closest('[data-cart-qty-input]');
    if (qtyInput) {
      const line = qtyInput.closest('[data-line-key]');
      const quantity = Math.max(0, parseInt(qtyInput.value, 10) || 0);
      if (line) changeLine(line.dataset.lineKey, quantity, line);
      return;
    }
    const note = e.target.closest('[data-cart-note]');
    if (note) {
      fetch(`${routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note.value })
      }).catch(() => {});
    }
  });

  window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCart(); });

  // Add to cart (product forms and quick add)
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form], form[data-quick-add]');
    if (!form || config.cartType === 'page' || !supportsDialog || !cartDrawer()) return;
    e.preventDefault();
    if (form.dataset.busy) return;
    form.dataset.busy = 'true';
    const button = form.querySelector('[type=submit]');
    const container = form.closest('[data-product-card]') || form;
    const message = $('.form-message', container);
    if (message) message.hidden = true;
    button?.classList.add('is-loading');
    button?.setAttribute('aria-busy', 'true');
    const body = new FormData(form);
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);
    try {
      const res = await fetch(`${routes.cartAdd}.js`, { method: 'POST', body, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(typeof data.description === 'string' ? data.description : (data.message || strings.error));
      if (data.sections && data.sections['cart-drawer']) renderCart(data.sections['cart-drawer']);
      else await refreshCart();
      if (form.matches('[data-quick-add]') && button) {
        button.classList.add('is-added');
        setTimeout(() => button.classList.remove('is-added'), 1500);
      }
      openCart(button);
      document.dispatchEvent(new CustomEvent('gobivo:cart-added', { detail: data }));
    } catch (err) {
      if (message) { message.textContent = err.message; message.hidden = false; }
      else toast(err.message, true);
    } finally {
      delete form.dataset.busy;
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-busy');
    }
  });

  // Quantity steppers (product forms)
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (!step) return;
    const input = $('[data-qty-input]', step.closest('[data-qty]'));
    if (!input) return;
    const min = Number(input.min) || 1;
    input.value = Math.max(min, (parseInt(input.value, 10) || min) + Number(step.dataset.qtyStep));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Product: variants, gallery, sticky bar ---------- */
  function initProduct(scope) {
    if (!scope || scope.dataset.productReady) return;
    scope.dataset.productReady = 'true';
    const json = $('[data-variant-json]', scope);
    const variants = json ? JSON.parse(json.textContent) : [];
    const picker = $('[data-variant-picker]', scope);
    const select = $('[data-variant-select]', scope);
    const form = $('form[data-product-form]', scope);
    const isMain = scope.hasAttribute('data-section-id');
    if (picker) scope.classList.add('has-picker');

    const gallery = initGallery($('[data-gallery]', scope));

    const selectedOptions = () => {
      const groups = {};
      $$('[data-option-index]', picker).forEach((input) => {
        const i = Number(input.dataset.optionIndex);
        if (input.tagName === 'SELECT') groups[i] = input.value;
        else if (input.checked) groups[i] = input.value;
      });
      return Object.keys(groups).sort().map((k) => groups[k]);
    };

    function markAvailability(selected) {
      if (!picker) return;
      $$('input[data-option-index]', picker).forEach((input) => {
        const i = Number(input.dataset.optionIndex);
        const available = variants.some((v) => v.available && v.options[i] === input.value && v.options.every((opt, j) => j === i || opt === selected[j]));
        input.nextElementSibling?.classList.toggle('is-unavailable', !available);
      });
      $$('fieldset.option-field', picker).forEach((fieldset) => {
        const checked = $('input:checked', fieldset);
        const label = $('[data-option-selected]', fieldset);
        if (checked && label) label.textContent = checked.value;
      });
    }

    function applyVariant(variant) {
      const button = $('[data-add-button]', scope);
      const label = $('[data-add-label]', scope);
      const priceWrap = $('.price-lg', scope) || $('[data-price-wrap]', scope);
      if (!variant) {
        if (button) button.disabled = true;
        if (label) label.textContent = strings.unavailable;
        updateSticky(null);
        return;
      }
      if (select) select.value = variant.id;
      if (priceWrap) {
        const price = $('[data-price]', priceWrap);
        const compare = $('[data-compare]', priceWrap);
        const saving = $('[data-saving]', priceWrap);
        if (price) price.textContent = variant.price;
        if (compare) { compare.textContent = variant.compare; compare.hidden = !variant.compare; }
        if (saving) { saving.textContent = variant.saving; saving.hidden = !variant.saving; }
        priceWrap.classList.toggle('is-sale', !!variant.compare);
        const unit = $('[data-unit-price]', priceWrap);
        if (unit) unit.hidden = true;
      }
      if (button) button.disabled = !variant.available;
      if (label) label.textContent = variant.available ? strings.addToCart : strings.soldOut;

      const inventory = $('[data-inventory]', scope);
      if (inventory) {
        const text = $('[data-inventory-text]', inventory);
        const threshold = Number(inventory.dataset.threshold) || 0;
        inventory.classList.remove('is-in', 'is-low', 'is-out');
        if (!variant.available) { inventory.classList.add('is-out'); text.textContent = inventory.dataset.outStock; }
        else if (variant.inventory !== null && variant.inventory > 0 && variant.inventory <= threshold) {
          inventory.classList.add('is-low'); text.textContent = inventory.dataset.lowStock.replace('[count]', variant.inventory);
        } else { inventory.classList.add('is-in'); text.textContent = inventory.dataset.inStock; }
      }
      const sku = $('[data-sku]', scope);
      if (sku) { sku.textContent = variant.sku || ''; $('[data-sku-wrap]', scope).hidden = !variant.sku; }

      if (isMain) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(history.state, '', url);
      }
      if (variant.media && gallery) gallery.goToMedia(variant.media);
      updateSticky(variant);
    }

    function updateSticky(variant) {
      const sticky = $('[data-sticky-buy]', scope);
      if (!sticky) return;
      const btn = $('button', sticky);
      const price = $('[data-sticky-price]', sticky);
      if (!variant) { btn.disabled = true; btn.textContent = strings.unavailable; return; }
      price.textContent = variant.price;
      btn.disabled = !variant.available;
      btn.textContent = variant.available ? strings.addToCart : strings.soldOut;
    }

    picker?.addEventListener('change', () => {
      const selected = selectedOptions();
      const variant = variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      markAvailability(selected);
      applyVariant(variant);
    });
    if (!picker && select) {
      select.addEventListener('change', () => applyVariant(variants.find((v) => String(v.id) === select.value)));
    }
    if (picker) markAvailability(selectedOptions());

    // Sticky add-to-cart
    const sticky = $('[data-sticky-buy]', scope);
    const addButton = form && $('[data-add-button]', form);
    if (sticky && addButton && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }).observe(addButton);
      $('[data-sticky-submit]', sticky)?.addEventListener('click', () => {
        if (form.requestSubmit) form.requestSubmit(); else form.submit();
      });
    }

    // Recently viewed history
    if (isMain && scope.dataset.productHandle) {
      const handle = scope.dataset.productHandle;
      const list = store.get('gobivo:recent', []).filter((h) => h !== handle);
      list.unshift(handle);
      store.set('gobivo:recent', list.slice(0, 12));
    }
  }

  function initGallery(gallery) {
    if (!gallery) return null;
    const track = $('[data-gallery-track]', gallery);
    const slides = $$('.gallery-slide', track);
    const thumbs = $$('[data-thumb-index]', gallery);
    const counter = $('[data-gallery-current]', gallery);
    const prev = $('[data-gallery-prev]', gallery);
    const next = $('[data-gallery-next]', gallery);
    let current = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));

    const setActive = (i) => {
      current = i;
      slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
      thumbs.forEach((t, n) => {
        t.classList.toggle('is-active', n === i);
        if (n === i) { t.setAttribute('aria-current', 'true'); t.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }
        else t.removeAttribute('aria-current');
      });
      if (counter) counter.textContent = i + 1;
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    };
    const goTo = (i, smooth = true) => {
      const index = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[index].offsetLeft, behavior: smooth && !reducedMotion ? 'smooth' : 'auto' });
      setActive(index);
    };
    track.addEventListener('scroll', debounce(() => {
      const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      if (i !== current && slides[i]) setActive(i);
    }, 60), { passive: true });
    thumbs.forEach((t) => t.addEventListener('click', () => goTo(Number(t.dataset.thumbIndex))));
    prev?.addEventListener('click', () => goTo(current - 1));
    next?.addEventListener('click', () => goTo(current + 1));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
    });
    $$('[data-zoom]', gallery).forEach((btn) => btn.addEventListener('click', () => openLightbox(btn)));
    if (current > 0) requestAnimationFrame(() => goTo(current, false)); else setActive(0);

    return {
      goToMedia(id) {
        const i = slides.findIndex((s) => s.dataset.mediaId === String(id));
        if (i > -1) goTo(i);
      }
    };
  }

  function openLightbox(btn) {
    if (!supportsDialog) { window.open(btn.dataset.zoom, '_blank'); return; }
    const dialog = document.createElement('dialog');
    dialog.className = 'modal lightbox';
    dialog.setAttribute('aria-label', btn.getAttribute('aria-label') || '');
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'modal-close';
    close.setAttribute('data-modal-close', '');
    close.setAttribute('aria-label', $('[data-modal-close]')?.getAttribute('aria-label') || 'Close');
    close.innerHTML = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
    const img = document.createElement('img');
    img.src = btn.dataset.zoom;
    img.alt = $('img', btn)?.alt || '';
    dialog.append(close, img);
    document.body.append(dialog);
    openDialog(dialog, btn);
  }

  /* ---------- Quick view ---------- */
  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (!trigger) return;
    const modal = $('#QuickView');
    const body = modal && $('[data-quick-view-body]', modal);
    if (!body || !supportsDialog) { window.location.href = trigger.dataset.quickView; return; }
    e.preventDefault();
    body.replaceChildren();
    body.classList.add('is-loading');
    openDialog(modal, trigger);
    try {
      const url = new URL(trigger.dataset.quickView, window.location.origin);
      url.searchParams.set('section_id', 'quick-view');
      const res = await fetch(url);
      if (!res.ok) throw new Error(res.status);
      const product = parseHTML(await res.text()).querySelector('[data-quick-view-product]');
      if (!product) throw new Error('empty');
      body.replaceChildren(product);
      initProduct(product);
      $('input, button, select', product)?.focus({ preventScroll: true });
    } catch (err) {
      window.location.href = trigger.dataset.quickView;
    } finally {
      body.classList.remove('is-loading');
    }
  });

  /* ---------- Share ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    const data = { title: btn.dataset.title, url: btn.dataset.url || window.location.href };
    try {
      if (navigator.share) await navigator.share(data);
      else { await navigator.clipboard.writeText(data.url); toast(strings.copied); }
    } catch (err) { /* share dismissed */ }
  });

  /* ---------- Recommendations ---------- */
  function initRecommendations(scope = document) {
    $$('[data-recommendations]', scope).forEach((section) => {
      if (section.dataset.ready || !section.dataset.url) return;
      section.dataset.ready = 'true';
      const load = async () => {
        try {
          const res = await fetch(section.dataset.url);
          if (!res.ok) return;
          const fresh = parseHTML(await res.text()).querySelector('[data-recommendations]');
          if (fresh && fresh.querySelector('.product-grid')) section.innerHTML = fresh.innerHTML;
        } catch (e) { /* recommendations are optional */ }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '0px 0px 600px 0px' });
        io.observe(section);
      } else load();
    });
  }

  /* ---------- Recently viewed ---------- */
  function initRecentlyViewed(scope = document) {
    $$('[data-recently-viewed]', scope).forEach(async (section) => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const grid = $('[data-recently-viewed-grid]', section);
      const limit = Number(section.dataset.limit) || 4;
      const handles = store.get('gobivo:recent', []).filter((h) => h && h !== section.dataset.current).slice(0, limit);
      if (!handles.length || !grid) return;
      const money = (cents) => {
        try { return new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency || 'USD' }).format(cents / 100); }
        catch (e) { return (cents / 100).toFixed(2); }
      };
      const results = await Promise.all(handles.map((h) => fetch(`${root}products/${encodeURIComponent(h)}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)));
      const products = results.filter(Boolean);
      if (!products.length) return;
      products.forEach((p) => {
        const item = document.createElement('div');
        item.className = 'grid-item';
        const card = document.createElement('article');
        card.className = 'product-card';
        const media = document.createElement('div');
        media.className = 'card-media';
        const link = document.createElement('a');
        link.className = 'card-image-link';
        link.href = p.url;
        link.tabIndex = -1;
        if (p.featured_image) {
          const img = document.createElement('img');
          const src = p.featured_image.startsWith('//') ? 'https:' + p.featured_image : p.featured_image;
          img.src = src + (src.includes('?') ? '&' : '?') + 'width=600';
          img.alt = p.title;
          img.loading = 'lazy';
          img.className = 'card-image';
          img.width = 600; img.height = 600;
          link.append(img);
        }
        media.append(link);
        const info = document.createElement('div');
        info.className = 'card-info';
        const title = document.createElement('h3');
        title.className = 'card-title';
        const titleLink = document.createElement('a');
        titleLink.href = p.url;
        titleLink.textContent = p.title;
        title.append(titleLink);
        const price = document.createElement('div');
        price.className = 'price' + (p.compare_at_price > p.price ? ' is-sale' : '');
        const current = document.createElement('span');
        current.className = 'price-current';
        current.textContent = money(p.price);
        price.append(current);
        if (p.compare_at_price > p.price) {
          const compare = document.createElement('s');
          compare.className = 'price-compare';
          compare.textContent = money(p.compare_at_price);
          price.append(compare);
        }
        info.append(title, price);
        card.append(media, info);
        item.append(card);
        grid.append(item);
      });
      section.hidden = false;
    });
  }

  /* ---------- Collection filters (AJAX) ---------- */
  function sectionIdFor(el) { return el.closest('[data-ajax-section]')?.dataset.ajaxSection; }

  async function renderFiltered(url, sectionEl, push = true) {
    const content = $('[data-ajax-content]', sectionEl);
    const id = sectionEl.dataset.ajaxSection;
    if (!content || !id) { window.location.href = url; return; }
    const wasOpen = !!$('.facets-panel.is-open', sectionEl);
    content.classList.add('is-loading');
    try {
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', id);
      const res = await fetch(fetchUrl);
      if (!res.ok) throw new Error(res.status);
      const fresh = parseHTML(await res.text()).querySelector('[data-ajax-content]');
      if (!fresh) throw new Error('empty');
      content.replaceWith(fresh);
      if (wasOpen) $('.facets-panel', fresh)?.classList.add('is-open');
      else closeFacets();
      if (push) history.pushState({ facets: true }, '', url);
      initGridToggle(sectionEl);
      if (!wasOpen) sectionEl.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    } catch (err) {
      window.location.href = url;
    }
  }

  function facetsUrl(form) {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => { if (value !== '') params.append(key, value); });
    const action = form.getAttribute('action') || window.location.pathname;
    const qs = params.toString();
    return qs ? `${action}?${qs}` : action;
  }

  const submitFacetsDebounced = debounce((form) => renderFiltered(facetsUrl(form), form.closest('[data-ajax-section]')), 600);
  document.addEventListener('change', (e) => {
    const form = e.target.closest('[data-facets-form]');
    if (!form || !form.closest('[data-ajax-section]')) return;
    if (e.target.matches('[data-debounce]')) submitFacetsDebounced(form);
    else renderFiltered(facetsUrl(form), form.closest('[data-ajax-section]'));
  });
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-facets-form]');
    if (!form || !form.closest('[data-ajax-section]')) return;
    e.preventDefault();
    renderFiltered(facetsUrl(form), form.closest('[data-ajax-section]'));
  });
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-facet-link]');
    if (link && sectionIdFor(link) && link.href) {
      e.preventDefault();
      renderFiltered(link.href, link.closest('[data-ajax-section]'));
      return;
    }
    if (e.target.closest('[data-facets-open]')) openFacets(e.target.closest('[data-ajax-section]'));
    if (e.target.closest('[data-facets-close]') || e.target.matches('.facets-backdrop')) closeFacets();
  });
  window.addEventListener('popstate', (e) => {
    const sectionEl = $('[data-ajax-section]');
    if (sectionEl && (e.state?.facets || $('[data-facets-form]', sectionEl))) renderFiltered(window.location.href, sectionEl, false);
  });
  function openFacets(sectionEl) {
    const panel = sectionEl && $('[data-facets-panel]', sectionEl);
    if (!panel) return;
    panel.classList.add('is-open');
    if (!$('.facets-backdrop')) {
      const backdrop = document.createElement('div');
      backdrop.className = 'facets-backdrop';
      document.body.append(backdrop);
    }
    document.body.classList.add('has-overlay');
    $('summary, input', panel)?.focus();
  }
  function closeFacets() {
    $$('.facets-panel.is-open').forEach((p) => p.classList.remove('is-open'));
    $('.facets-backdrop')?.remove();
    if (!$$('dialog[open]').length) document.body.classList.remove('has-overlay');
  }

  function initGridToggle(scope = document) {
    $$('[data-product-grid]', scope).forEach((grid) => {
      const section = grid.closest('[data-ajax-section]');
      const buttons = section ? $$('[data-grid-cols]', section) : [];
      if (!buttons.length) return;
      const apply = (cols) => {
        grid.classList.remove('cols-2', 'cols-3', 'cols-4');
        grid.classList.add(`cols-${cols}`);
        buttons.forEach((b) => b.classList.toggle('is-active', b.dataset.gridCols === String(cols)));
      };
      apply(store.get('gobivo:cols', grid.dataset.defaultCols || 3));
      buttons.forEach((b) => b.addEventListener('click', () => { apply(b.dataset.gridCols); store.set('gobivo:cols', b.dataset.gridCols); }));
    });
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal(scope = document) {
    const items = $$('[data-reveal]:not(.is-revealed)', scope);
    if (!document.body.classList.contains('has-reveal') || reducedMotion || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach((el) => io.observe(el));
  }

  /* ---------- Back to top ---------- */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-back-to-top]')) {
      window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
      $('#MainContent')?.focus({ preventScroll: true });
    }
  });

  /* ---------- Customer addresses ---------- */
  function updateProvinces(country) {
    const form = country.closest('form');
    const province = form && $('[data-province]', form);
    if (!province) return;
    let entries = [];
    try { entries = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch (e) { entries = []; }
    province.replaceChildren(...entries.map(([value, label]) => new Option(label, value)));
    const wrap = province.closest('[data-province-wrap]');
    if (wrap) wrap.hidden = entries.length === 0;
    province.disabled = entries.length === 0;
    if (province.dataset.default) province.value = province.dataset.default;
  }
  function initAddresses(scope = document) {
    $$('[data-country]', scope).forEach((country) => {
      if (country.dataset.default) country.value = country.dataset.default;
      updateProvinces(country);
    });
  }
  document.addEventListener('change', (e) => {
    const country = e.target.closest('[data-country]');
    if (country) updateProvinces(country);
  });
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-confirm]');
    if (form && !window.confirm(form.dataset.confirm)) e.preventDefault();
  });

  // Escape closes open disclosure menus
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if ($('.facets-panel.is-open')) { closeFacets(); return; }
    const detail = e.target.closest?.('details[open]');
    if (detail && !detail.closest('dialog')) { detail.open = false; $('summary', detail)?.focus(); }
    const overlay = $('[data-search-overlay]:not([hidden])');
    if (overlay) overlay.hidden = true;
  });

  /* ---------- Init ---------- */
  function init(scope = document) {
    initAnnouncement(scope);
    initPredictiveSearch(scope);
    $$('[data-product]', scope).forEach(initProduct);
    if (scope.matches?.('[data-product]')) initProduct(scope);
    initRecommendations(scope);
    initRecentlyViewed(scope);
    initGridToggle(scope);
    initReveal(scope);
    initAddresses(scope);
  }

  // Collapse footer menus on small screens (they stay open without JavaScript)
  if (window.matchMedia('(max-width: 760px)').matches) $$('.footer-collapse[open]').forEach((d) => { d.open = false; });

  initHeader();
  init(document);

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-header]')) initHeader();
    init(e.target);
  });
})();
